// Application shell (layout from the Codex visual refresh, 2026-11-04): a white sidebar
// with the official logo at its natural proportions and the workspace navigation on
// desktop; on phones a compact header (the full logo on Home only, never shrunk into a
// mark) and a bottom navigation bar. One header carries the persona control on every
// size, so there is exactly one "who am I acting as" control on the page.
import type { ReactNode } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import badreorLogo from '@/assets/badreor-logo.png'
import { SYNTHETIC_NOTICE } from '@/domain/catalogue'
import { IdentityControl } from '@/features/account'
import { STAGING_NOTICE } from '@/domain/identity'
import { env, isConfigured, isDemoMode } from '@/lib/env'
import { useMe, useSession } from '@/lib/queries'
import { SIGN_IN_WARNING_MS } from '@/lib/session'
import { SignInExpired, SignInWarning, useSignInRemaining } from './SignInClock'

type NavItem = { to: string; label: string; short: string; icon: ReactNode }

// Help & Learning is reachable from every page, with or without a persona session.
const NAV: NavItem[] = [
  { to: '/', label: 'Home', short: 'Home', icon: <><path d="m3 10 9-7 9 7v10H3z" /><path d="M9 20v-7h6v7" /></> },
  { to: '/permits', label: 'Permits', short: 'Permits', icon: <path d="M5 3h10l4 4v14H5zM14 3v5h5M9 12h6M9 16h6" /> },
  { to: '/board', label: 'Board', short: 'Board', icon: <path d="M3 4h18v16H3zM3 10h18M9 10v10" /> },
  { to: '/policy', label: 'Rules', short: 'Rules', icon: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="m8 9 1 1 2-2M14 9h3m-9 6 1 1 2-2M14 15h3" /></> },
  { to: '/learn', label: 'Help & Learning', short: 'Learn', icon: <path d="m3 7 9-4 9 4-9 4zM6 9v7c4 3 8 3 12 0V9M21 7v7" /> },
]

const PROGRESS_ITEM: NavItem = {
  to: '/learn/progress',
  label: 'Learning progress',
  short: 'Progress',
  icon: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
}

/** People & access (0022): invitations, accounts and site access, for account administrators. */
const PEOPLE_ITEM: NavItem = {
  to: '/admin/people',
  label: 'People & access',
  short: 'People',
  icon: <><circle cx="9" cy="8" r="3" /><path d="M3 20c.8-3.5 3.2-5 6-5s5.2 1.5 6 5" /><path d="M16 6.5a2.5 2.5 0 1 1 0 5M18 15c1.8.6 2.8 2.2 3 5" /></>,
}

/** The licence register (0021), for account administrators like the progress overview. */
const LICENCES_ITEM: NavItem = {
  to: '/admin/licences',
  label: 'Licence register',
  short: 'Licences',
  icon: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="11" r="2" /><path d="M6 16c.8-1.5 1.8-2 3-2s2.2.5 3 2M15 10h3M15 14h3" /></>,
}

/** Sites & units (0029), for registry administrators. */
const REGISTRY_ITEM: NavItem = {
  to: '/admin/registry',
  label: 'Sites & units',
  short: 'Sites',
  icon: <><path d="M3 21h18M5 21V9l7-5 7 5v12" /><path d="M9 21v-6h6v6" /></>,
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg className="va-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  )
}

/**
 * Staging (0022): a persistent strip on every page, from the build mode or, when signed in,
 * from the database's own environment kind (ptw_me), whichever says staging.
 */
export function StagingStrip() {
  const session = useSession()
  const me = useMe()
  const staging = env.mode === 'staging' || (Boolean(session.identityKey) && me.data?.environment_kind === 'staging')
  if (!staging) return null
  return (
    <div className="border-b border-warn/40 bg-warn-light px-4 py-1.5 text-center text-xs font-bold tracking-wide text-warn" role="note" data-testid="staging-strip">
      {STAGING_NOTICE}
    </div>
  )
}

export function SyntheticStrip() {
  if (!isDemoMode) return null
  return (
    <div className="synthetic-stripe border-b border-synthetic/30 px-4 py-1.5 text-center text-xs font-semibold text-synthetic">
      {SYNTHETIC_NOTICE}
    </div>
  )
}

/** The page the breadcrumb names, from the route. */
function pageLabel(pathname: string): string {
  if (pathname === '/learn/progress') return 'Learning progress'
  if (pathname === '/admin/licences') return 'Licence register'
  if (pathname.startsWith('/learn')) return 'Help & Learning'
  if (pathname === '/permits/new') return 'New permit'
  if (pathname.startsWith('/permits/')) return 'Permit record'
  if (pathname.startsWith('/permits')) return 'Permits'
  if (pathname.startsWith('/board')) return 'Board'
  if (pathname.startsWith('/policy')) return 'Rules'
  if (pathname.startsWith('/demo')) return 'Demo'
  if (pathname.startsWith('/about')) return 'About'
  if (pathname === '/admin/people') return 'People & access'
  if (pathname === '/admin/registry') return 'Sites & units'
  if (pathname.startsWith('/sign-in')) return 'Sign in'
  return 'Home'
}

/** Account administrators also see the learning-progress overview and the licence register in the sidebar. */
function useIsAccountAdmin(): boolean {
  const session = useSession()
  const me = useMe()
  return isConfigured && Boolean(session.identityKey) && Boolean(me.data?.global_capabilities?.includes('administer_accounts'))
}

/** Registry administrators see Sites & units (0029). */
function useIsRegistryAdmin(): boolean {
  const session = useSession()
  const me = useMe()
  return isConfigured && Boolean(session.identityKey) && Boolean(me.data?.global_capabilities?.includes('administer_registry'))
}

export function Shell() {
  const { pathname } = useLocation()
  const admin = useIsAccountAdmin()
  const registryAdmin = useIsRegistryAdmin()
  const route = pathname === '/' ? 'home' : 'page'
  const sideNav = admin ? [...NAV, PROGRESS_ITEM, LICENCES_ITEM] : NAV
  // One-hour sign-in: warn near the end; after it, every page except Demo asks the
  // person to sign in again before anything else.
  const clock = useSignInRemaining()
  const expired = isConfigured && clock.persona !== null && clock.remainingMs !== null && clock.remainingMs <= 0 && !pathname.startsWith('/demo') && !pathname.startsWith('/sign-in')
  const warn = isConfigured && !expired && clock.remainingMs !== null && clock.remainingMs > 0 && clock.remainingMs <= SIGN_IN_WARNING_MS
  return (
    <div className="ptw-app" data-route={route}>
      <a className="va-skip no-print" href="#main-content">
        Skip to main content
      </a>
      <SyntheticStrip />
      <StagingStrip />
      <div className="va-layout">
        <aside className="va-sidebar no-print">
          <NavLink to="/" className="va-brand" aria-label="BadrEOR Permit to Work - home">
            <img className="va-logo" src={badreorLogo} width={114} height={138} alt="Badr Enhanced Oil Recovery" />
            <span>Permit to Work</span>
          </NavLink>
          <p className="va-nav-label" id="workspace-nav-label">
            Workspace
          </p>
          <nav className="va-nav" aria-label="Main">
            {sideNav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === '/' || n.to === '/learn'} className="va-nav-item">
                <Icon>{n.icon}</Icon>
                <span>{n.label}</span>
              </NavLink>
            ))}
            {admin ? (
              <NavLink to={PEOPLE_ITEM.to} className="va-nav-item">
                <Icon>{PEOPLE_ITEM.icon}</Icon>
                <span>{PEOPLE_ITEM.label}</span>
              </NavLink>
            ) : null}
            {registryAdmin ? (
              <NavLink to={REGISTRY_ITEM.to} className="va-nav-item">
                <Icon>{REGISTRY_ITEM.icon}</Icon>
                <span>{REGISTRY_ITEM.label}</span>
              </NavLink>
            ) : null}
          </nav>
          <div className="va-sidebar-note">
            <strong>BadrEOR PTW</strong>
            <p>{isDemoMode ? 'Synthetic demonstration: fictitious people, sites and qualifications.' : 'Electronic permit to work.'}</p>
          </div>
        </aside>
        <div className="va-content">
          <header className="va-topbar no-print">
            <NavLink to="/" className="va-topbar-brand" aria-label="BadrEOR Permit to Work - home">
              <img className="va-logo" src={badreorLogo} width={114} height={138} alt="Badr Enhanced Oil Recovery" />
              <strong>Permit to Work</strong>
            </NavLink>
            <div className="va-breadcrumb">
              <span>BadrEOR</span> <span aria-hidden="true">/</span> <strong>{pageLabel(pathname)}</strong>
            </div>
            {/* The persona control (demo) or account menu is drawn for a dark surface; keep it on one. */}
            <div className="va-persona rounded-lg bg-brand-dark">
              <IdentityControl />
            </div>
          </header>
          <main className="va-main" id="main-content" tabIndex={-1}>
            {warn && clock.remainingMs !== null ? <SignInWarning remainingMs={clock.remainingMs} /> : null}
            {expired && clock.persona ? <SignInExpired persona={clock.persona} /> : <Outlet />}
          </main>
        </div>
      </div>
      <nav className="va-mobile-nav no-print" aria-label="Main">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/' || n.to === '/learn'} aria-label={n.label} className="va-nav-item">
            <Icon>{n.icon}</Icon>
            <span>{n.short}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
